/**
 * ErrorBoundary -- перехватывает необработанные ошибки рендеринга React-дерева
 * и показывает пользователю дружелюбный экран с возможностью перезагрузить страницу.
 *
 * Работает корректно в светлой и тёмной темах MUI.
 */
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary] Unhandled error:', error, errorInfo);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isDev = import.meta.env.DEV;

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            p: 3,
            bgcolor: 'background.default',
          }}
        >
          <Box sx={{ maxWidth: 560, width: '100%' }}>
            <Alert
              severity="error"
              variant="outlined"
              sx={{
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <AlertTitle sx={{ fontWeight: 700, fontSize: 18 }}>
                Произошла непредвиденная ошибка
              </AlertTitle>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Приложение столкнулось с ошибкой и не может продолжить работу.
                Попробуйте перезагрузить страницу.
              </Typography>

              {isDev && this.state.error && (
                <Box
                  component="pre"
                  sx={{
                    p: 1.5,
                    mb: 2,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    fontSize: 12,
                    overflow: 'auto',
                    maxHeight: 200,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {this.state.error.message}
                  {this.state.error.stack && `\n\n${this.state.error.stack}`}
                </Box>
              )}

              <Button
                variant="contained"
                color="error"
                onClick={this.handleReload}
                sx={{ textTransform: 'none' }}
              >
                Перезагрузить страницу
              </Button>
            </Alert>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
